import type { AuthSession } from '@sss/shared'
import { HttpClient } from './HttpClient'
import { EventBus } from './EventBus'

const DEVICE_KEY = 'sss.device_id'

function ensureDeviceId(): string {
  let id = localStorage.getItem(DEVICE_KEY)
  if (!id) {
    id = `dev_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`
    localStorage.setItem(DEVICE_KEY, id)
  }
  return id
}

let cached: AuthSession | null = null

// parent player-web 通过 postMessage 推 token 进来；iframe 优先用 parent 的 session 与服务器同步 user_id
let parentSessionPromise: Promise<AuthSession> | null = null
let parentSessionResolve: ((s: AuthSession) => void) | null = null

// 优先：URL ?token=&user_id= 直接注入（避免 postMessage timing race）
if (typeof window !== 'undefined') {
  try {
    const params = new URLSearchParams(window.location.search)
    const t = params.get('token')
    const uid = params.get('user_id')
    if (t && uid) {
      const session = {
        access_token: t,
        user_id: uid,
        display_name: `Player ${uid.slice(-4).toUpperCase()}`,
        avatar_url: null,
      } as unknown as AuthSession
      cached = session
      HttpClient.setToken(t)
    }
  } catch { /* ignore */ }
}

if (typeof window !== 'undefined') {
  window.addEventListener('message', (e) => {
    const data = e.data
    if (!data || typeof data !== 'object') return
    if (data.type === 'sss-auth' && typeof data.access_token === 'string' && typeof data.user_id === 'string') {
      const session = {
        access_token: data.access_token as string,
        user_id: data.user_id as string,
        display_name: (data.display_name as string | undefined) ?? `Player ${(data.user_id as string).slice(-4).toUpperCase()}`,
        avatar_url: null,
      } as unknown as AuthSession
      cached = session
      HttpClient.setToken(session.access_token)
      if (parentSessionResolve) {
        parentSessionResolve(session)
        parentSessionResolve = null
      }
      EventBus.emit('auth:ready', { user_id: session.user_id, display_name: session.display_name })
    }
  })
  // 通知 parent："iframe 准备好接收 token"
  setTimeout(() => {
    try { window.parent?.postMessage({ type: 'sss-iframe-ready' }, '*') } catch { /* ignore */ }
  }, 50)
}

export const AuthService = {
  /** Returns a usable session, creating a guest on first launch.
   *  优先等 parent 推 token；最多等 600ms，超时则 fallback 自己 guest 登录。 */
  async ensure(): Promise<AuthSession> {
    if (cached) return cached
    // 嵌入 iframe 时优先等 parent 推 token
    if (typeof window !== 'undefined' && window.parent !== window) {
      if (!parentSessionPromise) {
        parentSessionPromise = new Promise<AuthSession>((resolve) => {
          parentSessionResolve = resolve
        })
      }
      const winner = await Promise.race([
        parentSessionPromise,
        new Promise<null>((res) => setTimeout(() => res(null), 600)),
      ])
      if (winner) return winner as AuthSession
      if (cached) return cached
    }
    const deviceId = ensureDeviceId()
    const session = await HttpClient.post<AuthSession>('/api/v1/auth/guest', {
      device_id: deviceId,
    })
    HttpClient.setToken(session.access_token)
    cached = session
    EventBus.emit('auth:ready', {
      user_id: session.user_id,
      display_name: session.display_name,
    })
    return session
  },

  current(): AuthSession | null {
    return cached
  },

  reset(): void {
    cached = null
    HttpClient.setToken(null)
    localStorage.removeItem(DEVICE_KEY)
  },
}
